import type { Settings } from '@deizi/shared';
import { useState } from 'react';
import { Field } from '../../../components/Form';
import { Alert } from '../../../components/States';
import { useToast } from '../../../components/Toast';
import { errorMessage } from '../../../lib/messages';
import { useUpdateSettings } from '../../../lib/settings-api';
import { BrandKitCard, LogoCard } from './BrandCards';

type Business = Settings['business'];
const FIELDS: { key: keyof Business; label: string; placeholder?: string; span?: number }[] = [
  { key: 'shopName', label: 'Nome da loja' },
  { key: 'cnpj', label: 'CNPJ', placeholder: '00.000.000/0001-00' },
  { key: 'address', label: 'Endereço', placeholder: 'Rua, número, bairro', span: 2 },
  { key: 'city', label: 'Cidade', placeholder: 'Florianópolis · SC' },
  { key: 'whatsapp', label: 'WhatsApp', placeholder: '(48) 9 ____-____' },
  { key: 'contactEmail', label: 'E-mail de contato' },
  { key: 'instagram', label: 'Instagram', placeholder: '@ da loja' },
  { key: 'pixKey', label: 'Chave Pix', placeholder: 'Sai no PDF do orçamento' },
];

const KEYS: (keyof Business)[] = [...FIELDS.map((f) => f.key), 'regime'];

/** Negócio tab: business profile (no motivo; audited). */
export function BusinessTab({ settings, readOnly }: { settings: Settings; readOnly: boolean }) {
  const saved = settings.business;
  const [form, setForm] = useState<Business>(saved);
  const update = useUpdateSettings();
  const toast = useToast();
  const dirty = KEYS.some((k) => (form[k] ?? '') !== (saved[k] ?? ''));
  const set = (k: keyof Business, v: string) =>
    setForm((f) => ({ ...f, [k]: v === '' ? null : v }));
  const cls = (k: keyof Business) =>
    (form[k] ?? '') !== (saved[k] ?? '') ? 'input changed' : readOnly ? 'input readonly' : 'input';

  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 22, alignItems: 'flex-start' }}>
      <div className="col" style={{ flex: '3 1 520px', minWidth: 0, gap: 18 }}>
        <section className="card col" style={{ gap: 16 }}>
          <h2>Dados do negócio</h2>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit,minmax(min(220px,100%),1fr))',
              gap: 14,
            }}
          >
            {FIELDS.slice(0, 2).map((f) => (
              <Field key={f.key} id={`n-${f.key}`} label={f.label}>
                <input
                  id={`n-${f.key}`}
                  className={cls(f.key)}
                  value={form[f.key] ?? ''}
                  placeholder={f.placeholder}
                  onChange={(e) => set(f.key, e.target.value)}
                  disabled={readOnly}
                />
              </Field>
            ))}
            <Field
              id="n-regime"
              label="Regime"
              help="MEI: o imposto é o DAS fixo do mês (aba Custos)."
            >
              <select
                id="n-regime"
                className={cls('regime')}
                value={form.regime ?? ''}
                onChange={(e) => set('regime', e.target.value)}
                disabled={readOnly}
              >
                <option value="">Escolha</option>
                <option value="mei">MEI</option>
                <option value="simples">Simples Nacional</option>
                <option value="pf">Pessoa física</option>
              </select>
            </Field>
          </div>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit,minmax(min(220px,100%),1fr))',
              gap: 14,
            }}
          >
            {FIELDS.slice(2).map((f) => (
              <Field key={f.key} id={`n-${f.key}`} label={f.label}>
                <input
                  id={`n-${f.key}`}
                  className={cls(f.key)}
                  value={form[f.key] ?? ''}
                  placeholder={f.placeholder}
                  onChange={(e) => set(f.key, e.target.value)}
                  disabled={readOnly}
                />
              </Field>
            ))}
          </div>
          <p className="xs muted">Nome, CNPJ e contato saem no PDF do orçamento e no recibo.</p>
          {update.isError ? <Alert tone="err">{errorMessage(update.error)}</Alert> : null}
          {dirty && !readOnly ? (
            <div className="row" style={{ gap: 10, justifyContent: 'flex-end' }}>
              <button type="button" className="btn" onClick={() => setForm(saved)}>
                Descartar
              </button>
              <button
                type="button"
                className="btn p"
                disabled={update.isPending}
                onClick={() =>
                  update.mutate(
                    { business: form },
                    {
                      onSuccess: (s) => {
                        setForm(s.business);
                        toast('Dados do negócio salvos');
                      },
                    },
                  )
                }
              >
                Salvar dados do negócio
              </button>
            </div>
          ) : null}
        </section>
      </div>
      <aside className="col" style={{ flex: '2 1 320px', minWidth: 0, gap: 18 }}>
        <LogoCard readOnly={readOnly} />
        <BrandKitCard readOnly={readOnly} />
      </aside>
    </div>
  );
}
